import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';

import { AudienceTab } from '@/components/performance/tabs/audience-tab';
import { DataTable } from '@/components/glitch-ui/data-table';
import { platformsInScope, type Platform, type PlatformScope } from '@/lib/performance/types';
import { PlatformLogo } from '@/lib/performance/platform';
import { formatMoney, toDisplayAmount } from '@/lib/performance/money';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { useScopedCampaigns } from '@/hooks/usePerformanceData';
import { dateWindow } from '@/lib/performance/date-window';
import { metaIntegrationStatusQueryOptions, metaPlacementQueryOptions } from '@/lib/query-options';
import type { MetaPlacementRow } from '@/lib/types';
import {
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
} from '@/store';
import { cn } from '@/lib/utils';

type SubViewId = 'geography' | 'device' | 'demographics' | 'placement';

const GOOGLE_SUBVIEWS: { id: Exclude<SubViewId, 'placement'>; label: string }[] = [
  { id: 'geography', label: 'Geography' },
  { id: 'device', label: 'Device' },
  { id: 'demographics', label: 'Demographics' },
];

// Prettify Meta publisher_platform values for display.
const PLACEMENT_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  audience_network: 'Audience Network',
  messenger: 'Messenger',
  unknown: 'Unknown',
};

/** Meta Placement sub-view — a live publisher_platform breakdown (#6049). */
function PlacementSubView({ cs, dateRange }: { cs: string; dateRange: { start_date: string; end_date: string } }) {
  const { data, isLoading } = useQuery(metaPlacementQueryOptions(true, dateRange.start_date, dateRange.end_date));
  const rows = data?.rows ?? [];
  const columns = useMemo<ColumnDef<MetaPlacementRow, unknown>[]>(
    () => [
      { accessorKey: 'value', header: 'Placement', cell: ({ row }) => (
        <span className="font-medium text-gray-900">{PLACEMENT_LABELS[row.original.value] ?? row.original.value}</span>
      ) },
      { accessorKey: 'impressions', header: 'Impr.', cell: ({ row }) => row.original.impressions.toLocaleString() },
      { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
      { accessorKey: 'leads', header: 'Leads', cell: ({ row }) => row.original.leads.toLocaleString() },
      { accessorKey: 'spend_cents', header: 'Spend', cell: ({ row }) => formatMoney(toDisplayAmount(row.original.spend_cents, 'meta'), cs) },
      { id: 'cpl', accessorFn: (r) => r.cpl_cents ?? null, header: 'Cost / lead', cell: ({ row }) => (row.original.cpl_cents != null ? formatMoney(toDisplayAmount(row.original.cpl_cents, 'meta'), cs) : '—') },
      { id: 'ctr', accessorFn: (r) => r.ctr_pct ?? null, header: 'CTR', cell: ({ row }) => (row.original.ctr_pct != null ? `${row.original.ctr_pct.toFixed(2)}%` : '—') },
    ],
    [cs],
  );
  if (isLoading) return <div className="flex h-24 items-center justify-center text-sm text-gray-400">Loading…</div>;
  return <DataTable data={rows} columns={columns} pageSize={10} emptyState="No placement data for this period" />;
}

/**
 * Consolidated Audience tab (#5971, CP-9). Google sub-views (Geography / Device
 * / Demographics) reuse the Phase 1 AudienceTab account reports directly; Meta
 * contributes Placement. Sub-views are platform-native — they appear only when
 * their platform is in scope (Placement under Meta, the rest under Google).
 */
export function AudiencePanel({ scope }: { scope: PlatformScope }) {
  const inScope = platformsInScope(scope);
  const googleInScope = inScope.includes('google');
  const metaInScope = inScope.includes('meta');
  const showPlatformTag = inScope.length > 1;

  const items: { id: SubViewId; label: string; platform: Platform }[] = [
    ...(googleInScope ? GOOGLE_SUBVIEWS.map((s) => ({ ...s, platform: 'google' as const })) : []),
    ...(metaInScope ? [{ id: 'placement' as const, label: 'Placement', platform: 'meta' as const }] : []),
  ];

  const [subView, setSubView] = useState<SubViewId>('geography');
  const active: SubViewId = items.some((i) => i.id === subView) ? subView : items[0]?.id ?? 'geography';

  const { symbol } = useCurrencySymbol('google');
  const currencySymbol = symbol ?? '';
  // Meta money renders in the Meta AD ACCOUNT's currency, not the Google account's (#6163 P2,
  // #6182) — the same sourcing the Meta detail pages use. Falls back to the org default while the
  // status loads or when no account is selected.
  const { data: metaStatus } = useQuery({ ...metaIntegrationStatusQueryOptions(), enabled: metaInScope });
  const { symbol: metaCurrencySymbol } = useCurrencySymbol(undefined, metaStatus?.selected_ad_account?.currency);

  // Honor the toolbar filters through the shared scoping + window helpers
  // (#6281): the old private LIVE/PAUSED narrowing was exactly the accidental
  // filter #6128 removed from usePerformanceData — this tab reported on a
  // different campaign set than its siblings — and its private date math
  // diverged from the shared exact-day contract (#6274).
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);
  const { scopedGoogleAdsIds } = useScopedCampaigns({ includeExternal, typeFilter, selectedCampaignIds });
  const campaignIds = scopedGoogleAdsIds;
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const dateRange = useMemo(() => {
    const w = dateWindow(selectedDays);
    return { start_date: w.start_date, end_date: w.end_date };
  }, [selectedDays]);

  return (
    <div className="space-y-4">
      <div className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 p-0.5">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setSubView(item.id)}
            className={cn(
              'h-8 rounded-md px-3 text-sm font-medium transition-colors',
              item.id === active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
            )}
          >
            {showPlatformTag && (
              <PlatformLogo platform={item.platform} className="mr-1.5 inline-block h-3.5 w-3.5 align-text-bottom" />
            )}
            {item.label}
          </button>
        ))}
      </div>
      {active === 'placement' ? (
        <PlacementSubView cs={metaCurrencySymbol} dateRange={dateRange} />
      ) : campaignIds ? (
        <AudienceTab
          subView={active}
          campaignIds={campaignIds}
          currencySymbol={currencySymbol}
          dateRangeFilter={dateRange}
        />
      ) : (
        // Zero-match filter: an absent campaign_ids param would mean
        // account-wide to the backend (#6275) — show the truth instead.
        <div className="flex h-24 items-center justify-center text-sm text-gray-400">
          No campaigns match the current filter.
        </div>
      )}
    </div>
  );
}
